/**
 * Tenant Context Selector Component
 * Searchable dropdown in app header for selecting the current tenant context.
 * Updates X-Tenant-ID header for all Graph API requests.
 */

import React, { useMemo, useState } from 'react';
import {
  Combobox,
  Option,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { Building20Regular, CheckmarkCircle20Regular, ErrorCircle20Regular } from '@fluentui/react-icons';
import { useTenantContext } from '../../contexts/TenantContext';
import type { TenantListItem } from '../../types/tenant';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXS),
  },
  dropdown: {
    minWidth: '280px',
    maxWidth: '350px',
  },
  optionContent: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  tenantName: {
    fontWeight: tokens.fontWeightSemibold,
  },
  tenantDomain: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  accessIcon: {
    marginLeft: 'auto',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
});

/**
 * Tenant Context Selector Props
 */
interface TenantContextSelectorProps {
  /**
   * Optional callback when tenant selection changes
   */
  onTenantChange?: (tenant: TenantListItem | null) => void;

  /**
   * If true, excludes the home tenant from the dropdown.
   * Used in User Management page where only managed client tenants should be shown.
   */
  excludeHomeTenant?: boolean;
}

/**
 * Tenant Context Selector Component
 * Displays searchable dropdown for selecting active tenant.
 * Persists selection and updates global context.
 *
 * @example
 * ```tsx
 * <TenantContextSelector
 *   onTenantChange={(tenant) => console.log('Selected:', tenant)}
 * />
 * ```
 */
export const TenantContextSelector: React.FC<TenantContextSelectorProps> = ({
  onTenantChange,
  excludeHomeTenant = false,
}) => {
  const styles = useStyles();
  const { currentTenant, setCurrentTenant, tenantList, isLoading, error } =
    useTenantContext();
  const [searchQuery, setSearchQuery] = useState('');

  /**
   * Get access status icon for tenant
   */
  const getAccessIcon = (tenant: TenantListItem) => {
    if (tenant.has_access === true) {
      return <CheckmarkCircle20Regular className={styles.accessIcon} style={{ color: tokens.colorPaletteGreenForeground1 }} />;
    }
    if (tenant.has_access === false) {
      return <ErrorCircle20Regular className={styles.accessIcon} style={{ color: tokens.colorPaletteRedForeground1 }} />;
    }
    return null;
  };

  /**
   * Render tenant option content
   */
  const renderTenantOption = (tenant: TenantListItem) => (
    <div className={styles.optionContent}>
      <Building20Regular />
      <div>
        <div className={styles.tenantName}>{tenant.tenant_name}</div>
        <div className={styles.tenantDomain}>{tenant.domain}</div>
      </div>
      {getAccessIcon(tenant)}
    </div>
  );

  /**
   * Memoize filtered and sorted tenants.
   * Filtered by search query (matches tenant name or domain).
   * Sorted alphabetically by tenant name.
   */
  const filteredTenants = useMemo(() => {
    let filtered = excludeHomeTenant
      ? tenantList.filter((t) => t.status === 'managed')
      : tenantList.filter((t) => t.status === 'managed' || t.status === 'home');

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.tenant_name.toLowerCase().includes(query) ||
          t.domain.toLowerCase().includes(query)
      );
    }

    return filtered.sort((a, b) => a.tenant_name.localeCompare(b.tenant_name));
  }, [tenantList, excludeHomeTenant, searchQuery]);

  /**
   * Handle tenant selection from combobox
   */
  const handleOptionSelect = (
    _event: unknown,
    data: { optionValue?: string; optionText?: string }
  ) => {
    const selectedTenantId = data.optionValue;

    if (!selectedTenantId) {
      setCurrentTenant(null);
      onTenantChange?.(null);
      return;
    }

    const selectedTenant = tenantList.find((t) => t.tenant_id === selectedTenantId);
    if (selectedTenant) {
      setCurrentTenant(selectedTenant);
      onTenantChange?.(selectedTenant);
      setSearchQuery(''); // Clear search after selection
    }
  };

  /**
   * Handle search input change - uses event.target.value like SearchableMultiSelect
   */
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner size="tiny" />
        <span>Loading tenants...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ color: tokens.colorPaletteRedForeground1 }}>
        Error loading tenants: {error}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Combobox
        className={styles.dropdown}
        placeholder="Search tenants..."
        value={searchQuery || currentTenant?.tenant_name || ''}
        selectedOptions={currentTenant ? [currentTenant.tenant_id] : []}
        onOptionSelect={handleOptionSelect}
        onChange={handleInputChange}
        data-testid="tenant-selector"
        freeform
      >
        {filteredTenants.length === 0 ? (
          <Option key="no-tenants" value="" disabled text="No matching tenants">
            {searchQuery ? 'No matching tenants' : 'No tenants registered'}
          </Option>
        ) : (
          filteredTenants.map((tenant) => (
            <Option
              key={tenant.tenant_id}
              value={tenant.tenant_id}
              text={tenant.tenant_name}
              data-testid={`tenant-option-${tenant.tenant_id}`}
            >
              {renderTenantOption(tenant)}
            </Option>
          ))
        )}
      </Combobox>
    </div>
  );
};
